import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Image, CalendarDays, MapPin, 
  FileText, Eye, Users, Download, ChevronDown, ChevronUp,
  Send, X, CheckCircle, XCircle, Clock, AlertCircle, AlertTriangle, Award, Printer,
  Lock, Unlock, Archive
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';

const API_URL = "";

export const AdminExhibitions = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [exhibitions, setExhibitions] = useState([]);
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showExForm, setShowExForm] = useState(false);
  const [showCallForm, setShowCallForm] = useState(false);
  const [editingEx, setEditingEx] = useState(null);
  const [editingCall, setEditingCall] = useState(null);
  const [expandedEx, setExpandedEx] = useState(null);
  
  // Submission review state
  const [submissions, setSubmissions] = useState([]);
  const [viewingCall, setViewingCall] = useState(null);
  const [viewingSubmission, setViewingSubmission] = useState(null);
  
  // Judging state
  const [judgingExId, setJudgingExId] = useState(null);
  const [categories, setCategories] = useState([]);
  const [awards, setAwards] = useState([]);
  const [judges, setJudges] = useState([]);
  const [newCatName, setNewCatName] = useState('');
  const [generatedToken, setGeneratedToken] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);  // { kind, id, label }

  // Upload cleanup state
  const [cleanupPreview, setCleanupPreview] = useState(null); // { exhibitionId, ...preview }
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  
  const [exForm, setExForm] = useState({
    title: '', summary: '', description: '', start_date: '', end_date: '', 
    location: '115 Melrose Place, San Antonio, TX 78212', room: 'gallery', 
    fees_enabled: false, submission_fee: 0, per_artwork_fee: 0,
    hero_image: null, hero_image_preview: ''
  });
  const [callForm, setCallForm] = useState({
    exhibition_id: '', title: '', instructions: '', deadline: '', max_artworks: 5, fees_enabled: false, submission_fee: 0, per_artwork_fee: 0,
    dual_tier_fees_enabled: false, member_fee_flat: 0, non_member_fee_flat: 0, member_per_artwork_fee: 0, non_member_per_artwork_fee: 0, allow_non_member_submissions: true, currency: 'USD'
  });

  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [exRes, callRes] = await Promise.all([
        fetch(`${API_URL}/api/exhibitions/admin/all`, { headers }),
        fetch(`${API_URL}/api/exhibitions/admin/calls`, { headers })
      ]);
      setExhibitions(await exRes.json());
      setCalls(await callRes.json());
    } catch (e) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Exhibition CRUD
  const saveExhibition = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.entries(exForm).forEach(([k, v]) => {
      if (k === 'hero_image' && v instanceof File) fd.append('hero_image', v);
      else if (k === 'hero_image' || k === 'hero_image_preview') return;
      else if (k === 'fees_enabled') fd.append(k, v ? 'true' : 'false');
      else if (v !== null && v !== undefined && v !== '') fd.append(k, v);
    });
    
    const url = editingEx 
      ? `${API_URL}/api/exhibitions/admin/${editingEx.id}` 
      : `${API_URL}/api/exhibitions/admin`;
    
    try {
      const res = await fetch(url, { method: editingEx ? 'PUT' : 'POST', headers, body: fd });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success(editingEx ? 'Exhibition updated' : 'Exhibition created');
      setShowExForm(false);
      setEditingEx(null);
      setExForm({ title: '', summary: '', description: '', start_date: '', end_date: '', location: '115 Melrose Place, San Antonio, TX 78212', room: 'gallery', fees_enabled: false, submission_fee: 0, per_artwork_fee: 0, hero_image: null, hero_image_preview: '' });
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const deleteExhibition = async (id) => {
    const ex = exhibitions.find(e => e.id === id);
    setConfirmDel({ kind: 'exhibition', id, label: ex?.title || 'this exhibition' });
  };

  const performDeleteExhibition = async (id) => {
    await fetch(`${API_URL}/api/exhibitions/admin/${id}`, { method: 'DELETE', headers });
    toast.success('Exhibition deleted');
    fetchData();
  };

  // Call CRUD
  const saveCall = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.entries(callForm).forEach(([k, v]) => {
      if (k === 'fees_enabled' || k === 'dual_tier_fees_enabled' || k === 'allow_non_member_submissions') fd.append(k, v ? 'true' : 'false');
      else fd.append(k, v);
    });
    
    const url = editingCall
      ? `${API_URL}/api/exhibitions/admin/calls/${editingCall.id}`
      : `${API_URL}/api/exhibitions/admin/calls`;
    
    try {
      const res = await fetch(url, { method: editingCall ? 'PUT' : 'POST', headers, body: fd });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success(editingCall ? 'Call updated' : 'Call created');
      setShowCallForm(false);
      setEditingCall(null);
      setCallForm({ exhibition_id: '', title: '', instructions: '', deadline: '', max_artworks: 5, fees_enabled: false, submission_fee: 0, per_artwork_fee: 0, dual_tier_fees_enabled: false, member_fee_flat: 0, non_member_fee_flat: 0, member_per_artwork_fee: 0, non_member_per_artwork_fee: 0, allow_non_member_submissions: true, currency: 'USD' });
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const deleteCall = async (id) => {
    const call = calls.find(c => c.id === id);
    setConfirmDel({ kind: 'call', id, label: call?.title || 'this call for submissions' });
  };

  const performDeleteCall = async (id) => {
    await fetch(`${API_URL}/api/exhibitions/admin/calls/${id}`, { method: 'DELETE', headers });
    toast.success('Call deleted');
    fetchData();
  };

  // Submissions
  const viewSubmissions = async (callId) => {
    setViewingCall(callId);
    const res = await fetch(`${API_URL}/api/submissions/admin/all?call_id=${callId}`, { headers });
    setSubmissions(await res.json());
  };

  const viewSubmissionDetail = async (subId) => {
    const res = await fetch(`${API_URL}/api/submissions/admin/${subId}`, { headers });
    setViewingSubmission(await res.json());
  };

  const updateStatus = async (subId, status) => {
    try {
      const res = await fetch(`${API_URL}/api/submissions/admin/${subId}/status`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success(`Status updated to ${status}. Email sent.`);
      if (viewingCall) viewSubmissions(viewingCall);
      if (viewingSubmission) viewSubmissionDetail(subId);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const exportCSV = async (callId) => {
    try {
      const res = await fetch(`${API_URL}/api/submissions/admin/export/${callId}`, { headers });
      if (!res.ok) { toast.error('Export failed'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `submissions_${callId.slice(0, 8)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error('Export failed'); }
  };

  // Upload cleanup — hero image + submitted artwork images for a PAST
  // exhibition only. Member gallery/portfolio images are never in scope.
  const loadCleanupPreview = async (exId) => {
    setCleanupLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/exhibitions/admin/${exId}/upload-cleanup`, { headers });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Could not build cleanup preview'); return; }
      setCleanupPreview({ exhibitionId: exId, ...data });
    } catch (e) {
      toast.error('Network error building cleanup preview');
    } finally {
      setCleanupLoading(false);
    }
  };

  const runCleanup = async () => {
    if (!cleanupPreview) return;
    setCleanupRunning(true);
    try {
      const res = await fetch(`${API_URL}/api/exhibitions/admin/${cleanupPreview.exhibitionId}/upload-cleanup`, {
        method: 'POST', headers,
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || 'Cleanup failed'); return; }
      toast.success(data.message);
      setCleanupPreview(null);
    } catch (e) {
      toast.error('Network error running cleanup');
    } finally {
      setCleanupRunning(false);
    }
  };

  // Judging management
  const openJudging = async (exId) => {
    setJudgingExId(exId);
    const [catRes, awardRes, judgeRes] = await Promise.all([
      fetch(`${API_URL}/api/judging/categories/${exId}`, { headers }),
      fetch(`${API_URL}/api/judging/awards/${exId}`, { headers }),
      fetch(`${API_URL}/api/judging/judges/${exId}`, { headers })
    ]);
    setCategories(await catRes.json());
    setAwards(await awardRes.json());
    setJudges(await judgeRes.json());
    setGeneratedToken(null);
  };

  const addCategory = async () => {
    if (!newCatName.trim()) return;
    const res = await fetch(`${API_URL}/api/judging/categories`, {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ exhibition_id: judgingExId, name: newCatName.trim() })
    });
    if (res.ok) { setNewCatName(''); openJudging(judgingExId); toast.success('Category added'); }
    else toast.error((await res.json()).detail);
  };

  const deleteCategory = async (catId) => {
    const cat = categories.find(c => c.id === catId);
    setConfirmDel({ kind: 'category', id: catId, label: cat?.name || 'this category' });
  };

  const performDeleteCategory = async (catId) => {
    await fetch(`${API_URL}/api/judging/categories/${catId}`, { method: 'DELETE', headers });
    openJudging(judgingExId);
  };

  const generateToken = async () => {
    const res = await fetch(`${API_URL}/api/judging/judges/generate-token`, {
      method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ exhibition_id: judgingExId, expires_days: 30 })
    });
    if (res.ok) {
      const data = await res.json();
      setGeneratedToken(data);
      openJudging(judgingExId);
      toast.success('Judge token generated');
    } else toast.error((await res.json()).detail);
  };

  const revokeToken = async (judgeId) => {
    setConfirmDel({ kind: 'judge_token', id: judgeId, label: 'this judge access link' });
  };

  const performRevokeToken = async (judgeId) => {
    await fetch(`${API_URL}/api/judging/judges/${judgeId}`, { method: 'DELETE', headers });
    openJudging(judgingExId);
    toast.success('Token revoked');
  };

  const approveAward = async (awardId) => {
    const res = await fetch(`${API_URL}/api/judging/awards/${awardId}/approve`, { method: 'PUT', headers });
    if (res.ok) { openJudging(judgingExId); toast.success('Award approved'); }
    else toast.error((await res.json()).detail);
  };

  const approveAllAwards = async () => {
    const res = await fetch(`${API_URL}/api/judging/awards/approve-all/${judgingExId}`, { method: 'PUT', headers });
    if (res.ok) { openJudging(judgingExId); toast.success('All awards approved'); }
    else toast.error((await res.json()).detail);
  };

  const statusBadge = (status) => {
    const map = {
      submitted: { color: 'bg-blue-100 text-blue-800', icon: Clock },
      accepted: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle },
      waitlisted: { color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle }
    };
    const s = map[status] || map.submitted;
    return <Badge className={`${s.color} gap-1`}><s.icon size={12} />{status}</Badge>;
  };

  if (loading) return <AdminLayout title="Exhibitions"><div className="p-8 text-center">Loading...</div></AdminLayout>;

  // Judging Panel
  if (judgingExId) {
    const ex = exhibitions.find(e => e.id === judgingExId);
    const approvedAwards = awards.filter(a => a.status === 'approved');
    const submittedAwards = awards.filter(a => a.status === 'submitted');
    const groupedAwards = {};
    for (const a of awards) {
      if (!groupedAwards[a.category_name || 'Unknown']) groupedAwards[a.category_name || 'Unknown'] = [];
      groupedAwards[a.category_name || 'Unknown'].push(a);
    }

    // Determine judging completeness for Step 4
    const placements = ['1st', '2nd', '3rd'];
    const incompleteCategories = categories.filter(cat => {
      const catAwards = awards.filter(a => a.medium_category_id === cat.id);
      return placements.some(p => !catAwards.find(a => a.placement === p));
    });
    const allCategoriesComplete = categories.length > 0 && incompleteCategories.length === 0;
    const activeJudge = judges.find(j => j.is_active);

    return (
      <AdminLayout title="Judging" subtitle={ex?.title || ''}>
        <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
          {/* Top bar */}
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => setJudgingExId(null)} data-testid="back-from-judging">
              <X size={16} className="mr-1" /> Back to Exhibitions
            </Button>
            <Button variant="outline" onClick={() => navigate(`/admin/exhibitions/${judgingExId}/judge-preview`)} data-testid="view-as-judge-btn">
              <Eye size={16} className="mr-1" /> Open Admin Preview (not the judge link)
            </Button>
          </div>

          {/* Workflow explanation */}
          <Card className="bg-stone-50 border-stone-200">
            <CardContent className="py-4">
              <p className="text-base text-stone-700 font-medium">
                Judging Workflow:
              </p>
              <p className="text-sm text-stone-600 mt-1 leading-relaxed">
                1) Set categories &mdash; 2) Create and share a private Judge Link &mdash; 3) Monitor completion &mdash; 4) Approve &amp; publish winners.
              </p>
            </CardContent>
          </Card>

          {/* ── STEP 1: Set Categories ── */}
          <Card data-testid="categories-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-stone-900 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">1</span>
                Set Categories
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-4">
                {categories.map(c => (
                  <Badge key={c.id} className="bg-stone-100 text-stone-700 gap-2 px-3 py-1.5 text-sm">
                    {c.name}
                    <button onClick={() => deleteCategory(c.id)} className="text-red-400 hover:text-red-600">&times;</button>
                  </Badge>
                ))}
                {categories.length === 0 && (
                  <p className="text-stone-400 italic text-sm">No categories yet. Add at least one before judging.</p>
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newCatName}
                  onChange={e => setNewCatName(e.target.value)}
                  placeholder="e.g. Oil Painting"
                  className="max-w-xs"
                  data-testid="new-category-input"
                  onKeyDown={e => e.key === 'Enter' && addCategory()}
                />
                <Button size="sm" onClick={addCategory} data-testid="add-category-btn">
                  <Plus size={14} className="mr-1" /> Add
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* ── STEP 2: Provide Judge Access ── */}
          <Card data-testid="judge-token-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-stone-900 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">2</span>
                Provide Judge Access
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-stone-500 mb-4 leading-relaxed">
                This private link allows your selected judge to access the blind judging portal without creating an account.
                Do not share publicly.
              </p>

              {activeJudge ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4" data-testid="active-judge-link">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <Badge className="bg-green-100 text-green-800 text-sm mb-1">Active Judge Link</Badge>
                      <p className="text-xs text-green-600">Expires: {new Date(activeJudge.token_expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => {
                        const url = `${window.location.origin}/judge/${judgingExId}?token=${activeJudge.token_preview || ''}`;
                        navigator.clipboard.writeText(url);
                        toast.success('Judge link copied to clipboard!');
                      }} data-testid="copy-judge-link-btn">
                        Copy Link
                      </Button>
                      <Button size="sm" variant="outline" className="text-red-500 border-red-200 hover:bg-red-50" onClick={() => revokeToken(activeJudge.id)} data-testid="revoke-token-btn">
                        Revoke
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-stone-400 text-sm mb-4">No active judge link. Create one below to begin judging.</p>
              )}

              {generatedToken && (
                <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 mb-4" data-testid="generated-token-display">
                  <p className="font-medium text-yellow-800 mb-2">New Judge Link (shown once). Copy and share privately with your judge.</p>
                  <code className="block bg-white p-3 rounded text-sm break-all border">{window.location.origin}{generatedToken.judge_url}</code>
                  <Button size="sm" className="mt-2" onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}${generatedToken.judge_url}`);
                    toast.success('Copied!');
                  }}>Copy Link</Button>
                </div>
              )}

              {!activeJudge && (
                <Button onClick={generateToken} data-testid="generate-token-btn">
                  <Eye size={14} className="mr-1" /> Create Judge Link
                </Button>
              )}
            </CardContent>
          </Card>

          {/* ── STEP 3: Review Judging Progress ── */}
          <Card data-testid="judging-progress-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-stone-900 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">3</span>
                Review Judging Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              {categories.length === 0 ? (
                <p className="text-stone-400 italic text-sm">Add categories in Step 1 before judging can begin.</p>
              ) : (
                <div className="space-y-3">
                  {categories.map(cat => {
                    const catAwards = awards.filter(a => a.medium_category_id === cat.id);
                    const missing = placements.filter(p => !catAwards.find(a => a.placement === p));
                    const isComplete = missing.length === 0;
                    return (
                      <div key={cat.id} className={`flex items-center justify-between p-3 rounded-lg ${isComplete ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                        <span className="font-medium text-base text-stone-800">{cat.name}</span>
                        {isComplete ? (
                          <Badge className="bg-green-100 text-green-800 text-sm"><CheckCircle size={13} className="mr-1" /> Complete</Badge>
                        ) : (
                          <span className="text-sm text-amber-700">Missing: {missing.join(', ')}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {awards.length > 0 && (
                <div className="mt-6 space-y-6">
                  {Object.entries(groupedAwards).map(([catName, catAwards]) => (
                    <div key={catName}>
                      <h4 className="font-semibold text-stone-700 mb-3">{catName}</h4>
                      <div className="space-y-2">
                        {catAwards.sort((a, b) => a.placement.localeCompare(b.placement)).map(a => (
                          <div key={a.id} className="flex items-center justify-between bg-stone-50 p-3 rounded-lg" data-testid={`award-row-${a.id}`}>
                            <div className="flex items-center gap-3">
                              {a.artwork?.image_urls?.[0] && (
                                <img src={a.artwork.image_urls[0]} alt="" className="w-12 h-12 rounded object-cover" />
                              )}
                              <div>
                                <div className="flex items-center gap-2">
                                  <Badge className={a.placement === '1st' ? 'bg-yellow-500 text-white' : a.placement === '2nd' ? 'bg-stone-400 text-white' : 'bg-amber-700 text-white'}>{a.placement}</Badge>
                                  <span className="font-medium">{a.artwork?.title || 'Unknown'}</span>
                                </div>
                                <p className="text-xs text-stone-500 mt-0.5">{a.member_name || ''} &bull; {a.artwork?.medium || ''}</p>
                              </div>
                            </div>
                            <Badge className={a.status === 'approved' ? 'bg-green-100 text-green-800' : a.status === 'submitted' ? 'bg-blue-100 text-blue-800' : 'bg-stone-200 text-stone-600'}>{a.status}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* ── STEP 4: Approve & Publish ── */}
          <Card data-testid="awards-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="w-7 h-7 rounded-full bg-stone-900 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">4</span>
                Approve &amp; Publish
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {approvedAwards.length > 0 && (
                  <Button size="sm" variant="outline" onClick={async () => {
                    try {
                      const res = await fetch(`${API_URL}/api/judging/awards/${judgingExId}/export-csv`, { headers });
                      if (!res.ok) { toast.error('No approved winners to export'); return; }
                      const blob = await res.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `winners_${ex?.title?.toLowerCase().replace(/\s+/g, '-').slice(0, 30) || 'export'}.csv`;
                      a.click();
                      window.URL.revokeObjectURL(url);
                    } catch (e) { toast.error('Export failed'); }
                  }} data-testid="export-winners-csv-btn">
                    <Download size={14} className="mr-1" /> Export Winners CSV
                  </Button>
                )}

                {submittedAwards.length > 0 ? (
                  <Button onClick={approveAllAwards} className="bg-green-700 hover:bg-green-800 text-white" data-testid="approve-all-btn">
                    <CheckCircle size={14} className="mr-1" /> Approve &amp; Publish Winners
                  </Button>
                ) : (
                  <div className="flex items-center gap-3">
                    <Button disabled className="opacity-50 cursor-not-allowed" data-testid="approve-all-btn-disabled">
                      <CheckCircle size={14} className="mr-1" /> Approve &amp; Publish Winners
                    </Button>
                    {!allCategoriesComplete && (
                      <p className="text-sm text-stone-500">
                        {categories.length === 0
                          ? 'Add categories and generate a judge link first.'
                          : `All categories must have 1st, 2nd, and 3rd place selected (${categories.length - incompleteCategories.length}/${categories.length} complete).`}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  // Submission Detail Modal
  if (viewingSubmission) {
    const sub = viewingSubmission;
    return (
      <AdminLayout title="Submission Detail" subtitle={`${sub.display_name || sub.member_name} — ${sub.exhibition_title}`}>
        <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6">
          <div className="flex items-center gap-2 print:hidden">
            <Button variant="outline" onClick={() => setViewingSubmission(null)} data-testid="back-to-list"><X size={16} className="mr-1" /> Back to List</Button>
            <Button variant="outline" onClick={() => window.print()} data-testid="print-submission-btn"><Printer size={16} className="mr-1" /> Print</Button>
          </div>
          
          {/* Print-only header */}
          <div className="hidden print:block text-center mb-6">
            <h1 className="text-2xl font-serif font-bold">The Coppini Academy of Fine Art</h1>
            <p className="text-sm text-stone-500">Submission Reference — {sub.exhibition_title}</p>
            <hr className="mt-3" />
          </div>
          
          <Card className="print:shadow-none print:border-0">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle>{sub.display_name || sub.member_name}</CardTitle>
                  <p className="text-stone-500">{sub.display_email || sub.member_email}</p>
                  {sub.submitter_type === 'guest' && <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700 font-medium">Non-Member</span>}
                  <p className="text-stone-400 text-sm mt-1">Submitted: {new Date(sub.submitted_at).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-2" data-testid="status-actions">
                  {statusBadge(sub.status)}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-4 print:hidden">
                {['submitted', 'accepted', 'rejected', 'waitlisted'].map(s => (
                  <Button key={s} size="sm" variant={sub.status === s ? 'default' : 'outline'}
                    className={sub.status === s ? 'bg-stone-900' : ''}
                    onClick={() => updateStatus(sub.id, s)}
                    data-testid={`set-status-${s}`}
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </Button>
                ))}
              </div>
              
              {/* Lock Override Toggle */}
              <div className="flex items-center gap-3 mb-6 p-3 bg-stone-50 rounded-lg print:hidden" data-testid="lock-override-section">
                <span className="text-sm text-stone-600">Edit Lock:</span>
                {sub.is_locked_override ? (
                  <Button size="sm" variant="outline" className="text-amber-700 border-amber-300 bg-amber-50"
                    onClick={async () => {
                      try {
                        await fetch(`${API_URL}/api/submissions/admin/${sub.id}/lock-override`, {
                          method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ unlock: false })
                        });
                        setViewingSubmission({ ...sub, is_locked_override: false });
                        toast.success('Submission re-locked');
                      } catch (e) { toast.error('Failed to update lock'); }
                    }} data-testid="lock-btn">
                    <Lock size={14} className="mr-1" /> Re-lock
                  </Button>
                ) : (
                  <Button size="sm" variant="outline"
                    onClick={async () => {
                      try {
                        await fetch(`${API_URL}/api/submissions/admin/${sub.id}/lock-override`, {
                          method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
                          body: JSON.stringify({ unlock: true })
                        });
                        setViewingSubmission({ ...sub, is_locked_override: true });
                        toast.success('Submission unlocked for member editing');
                      } catch (e) { toast.error('Failed to update lock'); }
                    }} data-testid="unlock-btn">
                    <Unlock size={14} className="mr-1" /> Unlock for Editing
                  </Button>
                )}
                {sub.is_locked_override && (
                  <Badge className="bg-amber-100 text-amber-800 text-xs">Override active — member can edit past deadline</Badge>
                )}
              </div>
              
              <h3 className="text-lg font-semibold mb-4">Artworks ({sub.artworks?.length || 0})</h3>
              <div className="space-y-6">
                {(sub.artworks || []).map((aw, i) => (
                  <div key={aw.id || i} className="border rounded-lg p-4 print:break-inside-avoid">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                      <div><span className="text-xs text-stone-500">Title</span><p className="font-medium">{aw.title}</p></div>
                      <div><span className="text-xs text-stone-500">Size</span><p>{aw.size}</p></div>
                      <div><span className="text-xs text-stone-500">Medium</span><p>{aw.medium}</p></div>
                      <div><span className="text-xs text-stone-500">Price</span><p className="font-medium">${aw.price}</p></div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {(aw.image_urls || []).map((url, j) => (
                        <a key={j} href={url} target="_blank" rel="noopener noreferrer" className="block aspect-square rounded-lg overflow-hidden border hover:ring-2 ring-cafa-red transition-all print:break-inside-avoid">
                          <img src={url} alt={`${aw.title} ${j+1}`} className="w-full h-full object-cover" />
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  // Submissions List View
  if (viewingCall) {
    const call = calls.find(c => c.id === viewingCall);
    return (
      <AdminLayout title="Submissions" subtitle={call?.title || 'Call'}>
        <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <Button variant="outline" onClick={() => { setViewingCall(null); setSubmissions([]); }} data-testid="back-to-exhibitions"><X size={16} className="mr-1" /> Back</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => exportCSV(viewingCall)} data-testid="export-csv"><Download size={16} className="mr-1" /> Export CSV</Button>
              <Button variant="outline" onClick={async () => {
                try {
                  const callData = calls.find(c => c.id === viewingCall);
                  const exId = callData?.exhibition_id;
                  if (!exId) { toast.error('Exhibition not found'); return; }
                  toast.info('Preparing ZIP — this may take a moment...');
                  const res = await fetch(`${API_URL}/api/submissions/admin/download-images/${exId}`, { headers });
                  if (!res.ok) { toast.error((await res.json()).detail || 'Download failed'); return; }
                  const blob = await res.blob();
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `images_${viewingCall.slice(0, 8)}.zip`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                  toast.success('Download complete');
                } catch (e) { toast.error('Download failed'); }
              }} data-testid="download-all-images-btn"><Archive size={16} className="mr-1" /> Download All Images</Button>
            </div>
          </div>
          
          {submissions.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-stone-500">No submissions yet.</CardContent></Card>
          ) : (
            <div className="space-y-3">
              {submissions.map(sub => (
                <Card key={sub.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => viewSubmissionDetail(sub.id)} data-testid={`submission-row-${sub.id}`}>
                  <CardContent className="py-4">
                    <div className="flex items-center gap-4 flex-wrap">
                      {/* Thumbnails */}
                      {sub.thumbnail_urls?.length > 0 && (
                        <div className="flex -space-x-2 flex-shrink-0" data-testid={`thumbnails-${sub.id}`}>
                          {sub.thumbnail_urls.slice(0, 3).map((url, i) => (
                            <img key={i} src={url} alt="" className="w-12 h-12 rounded-lg object-cover border-2 border-white shadow-sm" />
                          ))}
                          {sub.thumbnail_urls.length > 3 && (
                            <div className="w-12 h-12 rounded-lg bg-stone-100 border-2 border-white shadow-sm flex items-center justify-center text-xs text-stone-500 font-medium">
                              +{sub.thumbnail_urls.length - 3}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold">{sub.display_name || sub.member_name}</p>
                        <p className="text-sm text-stone-500">{sub.display_email || sub.member_email}</p>
                        {sub.submitter_type === 'guest' && <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700 font-medium" data-testid={`guest-badge-${sub.id}`}>Non-Member</span>}
                      </div>
                      <div className="flex items-center gap-4 text-sm flex-shrink-0">
                        <span>{new Date(sub.submitted_at).toLocaleDateString()}</span>
                        <span>{sub.total_artworks} artwork{sub.total_artworks !== 1 ? 's' : ''}</span>
                        {statusBadge(sub.status)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </AdminLayout>
    );
  }

  // Main: Exhibitions + Calls Management
  return (
    <AdminLayout title="Exhibitions" subtitle="Manage exhibitions and calls for submissions">
      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-6">
        {/* Action Bar */}
        <div className="flex flex-wrap gap-3">
          <Button onClick={() => { setEditingEx(null); setExForm({ title: '', summary: '', description: '', start_date: '', end_date: '', location: '115 Melrose Place, San Antonio, TX 78212', room: 'gallery', fees_enabled: false, submission_fee: 0, per_artwork_fee: 0, hero_image: null, hero_image_preview: '' }); setShowExForm(true); }} data-testid="new-exhibition-btn">
            <Plus size={16} className="mr-1" /> New Exhibition
          </Button>
          {exhibitions.length > 0 && (
            <Button variant="outline" onClick={() => { setEditingCall(null); setCallForm({ exhibition_id: exhibitions[0]?.id || '', title: '', instructions: '', deadline: '', max_artworks: 5, fees_enabled: false, submission_fee: 0, per_artwork_fee: 0, dual_tier_fees_enabled: false, member_fee_flat: 0, non_member_fee_flat: 0, member_per_artwork_fee: 0, non_member_per_artwork_fee: 0, allow_non_member_submissions: true, currency: 'USD' }); setShowCallForm(true); }} data-testid="new-call-btn">
              <Send size={16} className="mr-1" /> New Call for Submissions
            </Button>
          )}
        </div>

        {/* Exhibition Form Modal */}
        {showExForm && (
          <Card className="border-cafa-red" data-testid="exhibition-form">
            <CardHeader><CardTitle>{editingEx ? 'Edit' : 'New'} Exhibition</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={saveExhibition} className="space-y-4">
                <div><Label>Title *</Label><Input value={exForm.title} onChange={e => setExForm({...exForm, title: e.target.value})} required data-testid="ex-title" /></div>
                <div><Label>Summary</Label><Input value={exForm.summary} onChange={e => setExForm({...exForm, summary: e.target.value})} data-testid="ex-summary" /></div>
                <div><Label>Description</Label><Textarea value={exForm.description} onChange={e => setExForm({...exForm, description: e.target.value})} rows={3} data-testid="ex-description" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Start Date *</Label><Input type="datetime-local" value={exForm.start_date?.slice(0,16)} onChange={e => setExForm({...exForm, start_date: e.target.value + ':00Z'})} required data-testid="ex-start-date" /></div>
                  <div><Label>End Date *</Label><Input type="datetime-local" value={exForm.end_date?.slice(0,16)} onChange={e => setExForm({...exForm, end_date: e.target.value + ':00Z'})} required data-testid="ex-end-date" /></div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Location</Label><Input value={exForm.location} onChange={e => setExForm({...exForm, location: e.target.value})} data-testid="ex-location" /></div>
                  <div>
                    <Label>Room</Label>
                    <select className="w-full h-10 mt-1 border rounded-md px-3 bg-white text-sm"
                      value={exForm.room} onChange={e => setExForm({...exForm, room: e.target.value})} data-testid="ex-room">
                      <option value="gallery">Gallery</option>
                      <option value="upstairs_gallery">Upstairs Gallery</option>
                      <option value="studio">Studio</option>
                    </select>
                  </div>
                </div>
                <div>
                  <Label>Exhibition Image</Label>
                  <div className="mt-1 flex items-center gap-4">
                    {(exForm.hero_image_preview || (exForm.hero_image instanceof File)) && (
                      <img
                        src={exForm.hero_image instanceof File ? URL.createObjectURL(exForm.hero_image) : exForm.hero_image_preview}
                        alt="Preview" className="w-24 h-16 object-cover rounded-lg border"
                      />
                    )}
                    <div className="flex-1">
                      <Input type="file" accept="image/jpeg,image/png,image/webp"
                        onChange={e => {
                          const file = e.target.files?.[0];
                          if (file) setExForm({...exForm, hero_image: file, hero_image_preview: ''});
                        }} data-testid="ex-hero-image" />
                      <p className="text-xs text-stone-400 mt-1">JPEG, PNG, or WebP. This image appears on the exhibitions listing page.</p>
                    </div>
                  </div>
                </div>
                {/* Fee Configuration */}
                <div className="border rounded-lg p-4 bg-stone-50">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={exForm.fees_enabled}
                      onChange={e => setExForm({...exForm, fees_enabled: e.target.checked})}
                      className="w-4 h-4 rounded" data-testid="ex-fees-enabled" />
                    <span className="font-medium text-stone-800">Enable Submission Fees</span>
                  </label>
                  {exForm.fees_enabled && (
                    <div className="grid grid-cols-2 gap-4 mt-3">
                      <div>
                        <Label>Flat Submission Fee ($)</Label>
                        <Input type="number" min="0" step="0.01" value={exForm.submission_fee}
                          onChange={e => setExForm({...exForm, submission_fee: parseFloat(e.target.value) || 0})}
                          data-testid="ex-submission-fee" />
                      </div>
                      <div>
                        <Label>Per Artwork Fee ($)</Label>
                        <Input type="number" min="0" step="0.01" value={exForm.per_artwork_fee}
                          onChange={e => setExForm({...exForm, per_artwork_fee: parseFloat(e.target.value) || 0})}
                          data-testid="ex-per-artwork-fee" />
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button type="submit" data-testid="ex-save-btn">Save Exhibition</Button>
                  <Button type="button" variant="outline" onClick={() => setShowExForm(false)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Call Form Modal */}
        {showCallForm && (
          <Card className="border-cafa-red" data-testid="call-form">
            <CardHeader><CardTitle>{editingCall ? 'Edit' : 'New'} Call for Submissions</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={saveCall} className="space-y-4">
                <div><Label>Exhibition *</Label>
                  <select className="w-full border rounded-md p-2" value={callForm.exhibition_id} onChange={e => setCallForm({...callForm, exhibition_id: e.target.value})} required data-testid="call-exhibition">
                    <option value="">Select exhibition...</option>
                    {exhibitions.map(ex => <option key={ex.id} value={ex.id}>{ex.title}</option>)}
                  </select>
                </div>
                <div><Label>Title *</Label><Input value={callForm.title} onChange={e => setCallForm({...callForm, title: e.target.value})} required data-testid="call-title" /></div>
                <div><Label>Instructions for Members</Label><Textarea value={callForm.instructions} onChange={e => setCallForm({...callForm, instructions: e.target.value})} rows={3} placeholder="Keep it simple and clear..." data-testid="call-instructions" /></div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Deadline *</Label><Input type="datetime-local" value={callForm.deadline?.slice(0,16)} onChange={e => setCallForm({...callForm, deadline: e.target.value + ':00Z'})} required data-testid="call-deadline" /></div>
                  <div><Label>Max Artworks</Label><Input type="number" min={1} max={10} value={callForm.max_artworks} onChange={e => setCallForm({...callForm, max_artworks: parseInt(e.target.value) || 5})} data-testid="call-max-artworks" /></div>
                </div>
                <div className="flex items-center gap-3">
                  <input type="checkbox" id="fees" checked={callForm.fees_enabled} onChange={e => setCallForm({...callForm, fees_enabled: e.target.checked})} />
                  <Label htmlFor="fees">Enable Legacy Fees (single tier)</Label>
                </div>
                {callForm.fees_enabled && (
                  <div className="grid grid-cols-2 gap-4 pl-4 border-l-2 border-cafa-red">
                    <div><Label>Submission Fee ($)</Label><Input type="number" min={0} step={0.01} value={callForm.submission_fee} onChange={e => setCallForm({...callForm, submission_fee: parseFloat(e.target.value) || 0})} /></div>
                    <div><Label>Per Artwork Fee ($)</Label><Input type="number" min={0} step={0.01} value={callForm.per_artwork_fee} onChange={e => setCallForm({...callForm, per_artwork_fee: parseFloat(e.target.value) || 0})} /></div>
                  </div>
                )}
                <div className="flex items-center gap-3 mt-2">
                  <input type="checkbox" id="dual_tier" checked={callForm.dual_tier_fees_enabled} onChange={e => setCallForm({...callForm, dual_tier_fees_enabled: e.target.checked})} />
                  <Label htmlFor="dual_tier">Enable Dual-Tier Fees (member vs non-member)</Label>
                </div>
                {callForm.dual_tier_fees_enabled && (
                  <div className="pl-4 border-l-2 border-cafa-red space-y-3">
                    <p className="text-sm text-stone-500 font-medium">Member Fees</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Member Flat Fee ($)</Label><Input type="number" min={0} step={0.01} value={callForm.member_fee_flat} onChange={e => setCallForm({...callForm, member_fee_flat: parseFloat(e.target.value) || 0})} data-testid="member-fee-flat" /></div>
                      <div><Label>Member Per Artwork ($)</Label><Input type="number" min={0} step={0.01} value={callForm.member_per_artwork_fee} onChange={e => setCallForm({...callForm, member_per_artwork_fee: parseFloat(e.target.value) || 0})} data-testid="member-per-artwork-fee" /></div>
                    </div>
                    <p className="text-sm text-stone-500 font-medium">Non-Member Fees</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Non-Member Flat Fee ($)</Label><Input type="number" min={0} step={0.01} value={callForm.non_member_fee_flat} onChange={e => setCallForm({...callForm, non_member_fee_flat: parseFloat(e.target.value) || 0})} data-testid="non-member-fee-flat" /></div>
                      <div><Label>Non-Member Per Artwork ($)</Label><Input type="number" min={0} step={0.01} value={callForm.non_member_per_artwork_fee} onChange={e => setCallForm({...callForm, non_member_per_artwork_fee: parseFloat(e.target.value) || 0})} data-testid="non-member-per-artwork-fee" /></div>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3 mt-2">
                  <input type="checkbox" id="allow_nonmember" checked={callForm.allow_non_member_submissions} onChange={e => setCallForm({...callForm, allow_non_member_submissions: e.target.checked})} />
                  <Label htmlFor="allow_nonmember">Allow Non-Member Submissions</Label>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" data-testid="call-save-btn">Save Call</Button>
                  <Button type="button" variant="outline" onClick={() => setShowCallForm(false)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Exhibitions List */}
        {exhibitions.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-stone-500"><FileText size={48} className="mx-auto mb-4 text-stone-300" /><p>No exhibitions yet. Create your first one above.</p></CardContent></Card>
        ) : (
          <div className="space-y-4">
            {exhibitions.map(ex => {
              const exCalls = calls.filter(c => c.exhibition_id === ex.id);
              const isExpanded = expandedEx === ex.id;
              const totalSubmissions = exCalls.reduce((sum, c) => sum + (c.submission_count || 0), 0);
              return (
                <Card key={ex.id} data-testid={`exhibition-card-${ex.id}`}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 cursor-pointer" onClick={() => setExpandedEx(isExpanded ? null : ex.id)}>
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold">{ex.title}</h3>
                          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-sm text-stone-500 mt-1">
                          <span className="flex items-center gap-1"><CalendarDays size={14} /> {new Date(ex.start_date).toLocaleDateString()} – {new Date(ex.end_date).toLocaleDateString()}</span>
                          <span className="flex items-center gap-1"><MapPin size={14} /> {ex.location}</span>
                          <Badge variant="outline">{exCalls.length} call{exCalls.length !== 1 ? 's' : ''}</Badge>
                          {totalSubmissions > 0 && (
                            <Badge className="bg-blue-100 text-blue-800" data-testid={`submission-count-${ex.id}`}>
                              <Users size={12} className="mr-1" /> {totalSubmissions} submission{totalSubmissions !== 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1 items-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setExpandedEx(isExpanded ? null : ex.id)}
                          data-testid={`exhibition-toggle-${ex.id}`}
                          aria-expanded={isExpanded}
                          aria-controls={`exhibition-details-${ex.id}`}
                          aria-label={isExpanded ? 'Hide exhibition details' : 'Show exhibition details (calls and judging)'}
                          title={isExpanded ? 'Hide details' : 'Show calls & judging'}
                          className="text-stone-600"
                        >
                          {isExpanded ? <><ChevronUp size={14} className="mr-1" /> Hide</> : <><ChevronDown size={14} className="mr-1" /> Details</>}
                        </Button>
                        <Button
                          size="sm" variant="ghost"
                          aria-label="Edit exhibition"
                          title="Edit exhibition"
                          onClick={() => { setEditingEx(ex); setExForm({ title: ex.title, summary: ex.summary || '', description: ex.description || '', start_date: ex.start_date, end_date: ex.end_date, location: ex.location, room: ex.room || 'gallery', fees_enabled: ex.fees_enabled || false, submission_fee: ex.submission_fee || 0, per_artwork_fee: ex.per_artwork_fee || 0, hero_image: null, hero_image_preview: ex.hero_image_url || '' }); setShowExForm(true); }}
                        ><Edit size={14} /></Button>
                        <Button
                          size="sm" variant="ghost"
                          className="text-red-500"
                          aria-label="Delete exhibition"
                          title="Delete exhibition"
                          onClick={() => deleteExhibition(ex.id)}
                        ><Trash2 size={14} /></Button>
                      </div>
                    </div>
                    
                    {isExpanded && (
                      <div className="mt-4 pt-4 border-t space-y-3" id={`exhibition-details-${ex.id}`}>
                        {ex.summary && <p className="text-stone-600">{ex.summary}</p>}
                        
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <h4 className="font-medium text-stone-700">Calls for Submissions</h4>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => openJudging(ex.id)} data-testid={`judging-btn-${ex.id}`} aria-label="Manage judging" title="Manage judging — categories, awards, and judge tokens"><Award size={14} className="mr-1" /> Manage Judging</Button>
                            {new Date(ex.end_date) < new Date() && (
                              <Button
                                size="sm" variant="outline"
                                className="text-amber-700 border-amber-300 hover:bg-amber-50"
                                onClick={() => loadCleanupPreview(ex.id)}
                                disabled={cleanupLoading}
                                data-testid={`cleanup-uploads-btn-${ex.id}`}
                                title="Remove hero image + submitted artwork images now that this exhibition is over. Member gallery images are never affected."
                              >
                                <Archive size={14} className="mr-1" /> Clean Up Uploads
                              </Button>
                            )}
                            <Button size="sm" variant="outline" onClick={() => {
                            setEditingCall(null);
                            setCallForm({ exhibition_id: ex.id, title: `Call for Submissions: ${ex.title}`, instructions: '', deadline: '', max_artworks: 5, fees_enabled: false, submission_fee: 0, per_artwork_fee: 0, dual_tier_fees_enabled: false, member_fee_flat: 0, non_member_fee_flat: 0, member_per_artwork_fee: 0, non_member_per_artwork_fee: 0, allow_non_member_submissions: true, currency: 'USD' });
                            setShowCallForm(true);
                          }}><Plus size={14} className="mr-1" /> Add Call</Button>
                          </div>
                        </div>
                        
                        {exCalls.length === 0 ? (
                          <p className="text-sm text-stone-400 italic">No calls yet</p>
                        ) : (
                          exCalls.map(call => (
                            <div key={call.id} className="bg-stone-50 rounded-lg p-4 space-y-3" data-testid={`call-row-${call.id}`}>
                              <div className="flex items-center justify-between flex-wrap gap-3">
                                <div>
                                  <p className="font-medium">{call.title}</p>
                                  <div className="flex items-center gap-3 text-sm text-stone-500 mt-1">
                                    <span>Deadline: {new Date(call.deadline).toLocaleDateString()}</span>
                                    <Badge className={call.is_open ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>{call.is_open ? 'Open' : 'Closed'}</Badge>
                                    <span>{call.submission_count || 0} submissions</span>
                                  </div>
                                </div>
                                <div className="flex gap-1">
                                  <Button size="sm" variant="outline" onClick={() => viewSubmissions(call.id)} data-testid={`view-submissions-${call.id}`} aria-label={`View ${call.submission_count || 0} submissions`} title="View submissions"><Users size={14} className="mr-1" /> View ({call.submission_count || 0})</Button>
                                  <Button size="sm" variant="ghost" aria-label="Edit call" title="Edit call" onClick={() => { setEditingCall(call); setCallForm({ exhibition_id: call.exhibition_id, title: call.title, instructions: call.instructions || '', deadline: call.deadline, max_artworks: call.max_artworks, fees_enabled: call.fees_enabled, submission_fee: call.submission_fee || 0, per_artwork_fee: call.per_artwork_fee || 0, dual_tier_fees_enabled: call.dual_tier_fees_enabled || false, member_fee_flat: call.member_fee_flat || 0, non_member_fee_flat: call.non_member_fee_flat || 0, member_per_artwork_fee: call.member_per_artwork_fee || 0, non_member_per_artwork_fee: call.non_member_per_artwork_fee || 0, allow_non_member_submissions: call.allow_non_member_submissions !== false, currency: call.currency || 'USD' }); setShowCallForm(true); }}><Edit size={14} /></Button>
                                  <Button size="sm" variant="ghost" className="text-red-500" aria-label="Delete call" title="Delete call" onClick={() => deleteCall(call.id)}><Trash2 size={14} /></Button>
                                </div>
                              </div>

                              {/* Submission Links (Part 4) */}
                              <div className="border-t border-stone-200 pt-3 space-y-2">
                                <p className="text-xs font-medium text-stone-500 uppercase tracking-wide">Submission Links</p>
                                <div className="flex flex-wrap gap-2">
                                  <Button size="sm" variant="outline" className="text-xs" onClick={() => {
                                    navigator.clipboard.writeText(`${window.location.origin}/members/submissions/${call.id}`);
                                    toast.success('Member submission link copied!');
                                  }} data-testid={`copy-member-link-${call.id}`}>
                                    Copy Member Link
                                  </Button>
                                  {call.allow_non_member_submissions !== false && (
                                    <Button size="sm" variant="outline" className="text-xs" onClick={() => {
                                      navigator.clipboard.writeText(`${window.location.origin}/submissions/guest/${call.id}`);
                                      toast.success('Guest submission link copied!');
                                    }} data-testid={`copy-guest-link-${call.id}`}>
                                      Copy Guest Link
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Generic Delete / Revoke Confirmation */}
      <AlertDialog open={!!confirmDel} onOpenChange={(open) => { if (!open) setConfirmDel(null); }}>
        <AlertDialogContent data-testid="exhibition-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="text-red-600" size={20} />
              {confirmDel?.kind === 'exhibition' && 'Delete this exhibition?'}
              {confirmDel?.kind === 'call' && 'Delete this call for submissions?'}
              {confirmDel?.kind === 'category' && 'Delete this category?'}
              {confirmDel?.kind === 'judge_token' && 'Revoke this judge access link?'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base text-stone-700 space-y-2">
              <span className="block">
                <strong>{confirmDel?.label}</strong>
              </span>
              {confirmDel?.kind === 'exhibition' && (
                <span className="block text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                  Deleting an exhibition <strong>also removes all its calls for submissions</strong>, judging categories, awards, and judge tokens. Submitted artwork records remain in the database but lose their parent context. This cannot be undone.
                </span>
              )}
              {confirmDel?.kind === 'call' && (
                <span className="block text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                  Submissions tied to this call will be orphaned. Submitters' artwork files remain, but the call's instructions, deadline, and fee config are removed permanently.
                </span>
              )}
              {confirmDel?.kind === 'category' && (
                <span className="block text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                  Awards within this category will be deleted. Judges' selections in this category are lost. This cannot be undone.
                </span>
              )}
              {confirmDel?.kind === 'judge_token' && (
                <span className="block text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                  The current judge link will stop working immediately. The judge's existing selections are <strong>not</strong> deleted — only their access. You can generate a new token to give them access again.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="exhibition-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={confirmDel?.kind === 'judge_token' ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
              onClick={() => {
                if (!confirmDel) return;
                const { kind, id } = confirmDel;
                setConfirmDel(null);
                if (kind === 'exhibition') performDeleteExhibition(id);
                if (kind === 'call') performDeleteCall(id);
                if (kind === 'category') performDeleteCategory(id);
                if (kind === 'judge_token') performRevokeToken(id);
              }}
              data-testid="exhibition-confirm-action"
            >
              {confirmDel?.kind === 'judge_token' ? 'Revoke Link' : 'Yes, Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Upload Cleanup Preview */}
      <Dialog open={!!cleanupPreview} onOpenChange={(open) => { if (!open) setCleanupPreview(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="cleanup-preview-dialog">
          <DialogHeader>
            <DialogTitle>Clean Up Uploads — {cleanupPreview?.exhibition_title}</DialogTitle>
            <DialogDescription>
              Removes the hero image and submitted artwork images for this exhibition to reclaim
              Cloudinary storage. Submission records (titles, mediums, prices, awards) stay — only the
              image files go. Member gallery/portfolio images are never touched.
            </DialogDescription>
          </DialogHeader>

          {cleanupPreview && !cleanupPreview.eligible && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3" data-testid="cleanup-not-eligible">
              {cleanupPreview.reason}
            </p>
          )}

          {cleanupPreview?.eligible && (
            <>
              {cleanupPreview.already_cleaned && (
                <p className="text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded p-3" data-testid="cleanup-already-done">
                  This exhibition's uploads were already cleaned up on {new Date(cleanupPreview.cleaned_at).toLocaleDateString()}.
                  Running it again only affects anything uploaded since.
                </p>
              )}
              <p className="text-sm text-stone-600" data-testid="cleanup-image-count">
                {cleanupPreview.image_count} image{cleanupPreview.image_count !== 1 ? 's' : ''} found.
              </p>
              <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto" data-testid="cleanup-image-grid">
                {cleanupPreview.images.map((img, i) => (
                  <div key={i} className="text-center" data-testid={`cleanup-image-${i}`}>
                    <img src={img.url} alt={img.kind} className="w-full h-16 object-cover rounded border border-stone-200" loading="lazy" />
                    <p className="text-[10px] text-stone-500 mt-0.5">{img.kind === 'hero_image' ? 'Hero' : (img.title || 'Artwork')}</p>
                    {img.shared_elsewhere ? (
                      <p className="text-[9px] text-amber-600">kept — used elsewhere</p>
                    ) : (
                      <p className="text-[9px] text-red-600">will delete</p>
                    )}
                  </div>
                ))}
                {cleanupPreview.image_count === 0 && (
                  <p className="col-span-full text-sm text-stone-500 py-4 text-center">No uploads to clean up.</p>
                )}
              </div>
            </>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCleanupPreview(null)} data-testid="cleanup-cancel-btn">Cancel</Button>
            {cleanupPreview?.eligible && cleanupPreview.image_count > 0 && (
              <Button
                className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={runCleanup}
                disabled={cleanupRunning}
                data-testid="cleanup-confirm-btn"
              >
                {cleanupRunning ? 'Cleaning up...' : `Delete ${cleanupPreview.images.filter(i => !i.shared_elsewhere).length} Image(s)`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminExhibitions;
